import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { enableMapSet } from "immer";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "semantic-ui-css/semantic.min.css";
import "swiper/css/bundle";
import "./App.css";
import AuthGuard from "./auth/AuthGuard";
import { FORMS, MODULES, statusConst } from "./const";
import { CustomBrowserRouter } from "./router/CustomBrowserRouter";
import Chart from "./screens/Chart";
import Dashboard from "./screens/Dashboard";
import Document from "./screens/Document";
import FormExample from "./screens/FormExample";
import GridExample from "./screens/GridExample";
import Layout from "./screens/Layout";
import Login from "./screens/Login";
import NotFound from "./screens/NotFound";
import PasswordRecoveryWizard from "./screens/Recovery/PasswordRecoveryWizard";
import Unauthorized from "./screens/Unauthorized";
import Wizard from "./screens/Wizard";
import ApprovalProcess from "./screens/finance/ApprovalProcess";
import ChargeConfirmation from "./screens/finance/ChargeConfirmation";
import ChargeCreation from "./screens/finance/ChargeCreation";
import DeductionMaster from "./screens/finance/DeductionMaster";
import EvinvGenerator from "./screens/finance/EVINVGenerator";
import ExchangeRate from "./screens/finance/ExchangeRate";
import InvoiceSearch from "./screens/finance/InvoiceSearch";
import RateManager from "./screens/finance/RateManager";
import TaxManagement from "./screens/finance/TaxManagement";
import DocumentManagement from "./screens/fleet/DocumentManagement";
import FleetDashboard from "./screens/fleet/FleetDashboard";
import Account from "./screens/master/Account";
import Agreements from "./screens/master/Agreements";
import AgreementTemplate from "./screens/master/AgreementTemplate";
import WorkOrder from "./screens/fleet/WorkOrder";
import ApprovalMaster from "./screens/master/ApprovalMaster";
import BlackList from "./screens/master/BlackList";
import ClusterMaster from "./screens/master/ClusterMaster";
import DeductionItem from "./screens/master/DeductionItem";
import DynamicTemplate from "./screens/master/DynamicTemplate";
import Employee from "./screens/master/Employee";
import Geographical from "./screens/master/Geographical";

import LocationCreation from "./screens/master/LocationCreation";
import Stakeholder from "./screens/master/Stakeholder";
import StakeholderGroup from "./screens/master/StakeholderGroup";
import Vehicle from "./screens/master/Vehicle";
import VehicleType from "./screens/master/VehicleType";
import Bank from "./screens/master/Bank";
import BankBranch from "./screens/master/BankBranch";
import MissionControl from "./screens/mission-control/MissionControl";
import JobMonitor from "./screens/transaction/JobMonitor";
import NCR from "./screens/transaction/NCR";
import RequestApproval from "./screens/transaction/RequestApproval";
import RequestCreationNew from "./screens/transaction/RequestCreationNew";
import { loadPermissions } from "./store/user/user-actions";
import GSDivision from "./screens/master/GSDivision";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import MobileCallback from "./components/MobileCallback";
import ShortenLinkCallback from "./components/ShortenLinkCallback";
import ChargeType from "./components/finance/ChargeType";
import { NotificationProvider } from "./components/notification/NotificationProvider";
import SupplierNegotiationApproval from "./components/transaction/approval/supplier-negotiation/SupplierNegotiationApproval";
import AuditReportSearch from "./components/transaction/audit-report/AuditReportSearch";
import useUserCompany from "./hooks/useUserCompany";
import Alert from "./screens/Admin/Alert";
import OptionConfig from "./screens/Admin/OptionConfig";
import CreditDebitNote from "./screens/finance/CreditDebitNote";
import EvInvApproval from "./screens/finance/EvInvApproval";
import OracleProcessQueue from "./screens/finance/OracleProcessQueue";
import TransporterInvoice from "./screens/finance/TransporterInvoice";
import ShiftGroupMaster from "./screens/master/ShiftGroupMaster";
import ShiftMaster from "./screens/master/ShiftMaster";
import Trailers from "./screens/master/Trailer";
import PoliceDivision from "./screens/master/PoliceDivision";
import CallCenterPerformance from "./screens/mis/CallCenterPerformance";
import DeductionReport from "./screens/mis/DeductionReport";
import DocumentDeliveryNote from "./screens/mis/DocumentDeliveryNote";
import FinanceAnalytics from "./screens/mis/FinanceAnalytics";
import LiveAnalytics from "./screens/mis/LiveAnalytics";
import PerformanceMonitoring from "./screens/mis/PerformanceMonitoring";
import ReportGenerator from "./screens/mis/ReportGenerator";
import ReportQueue from "./screens/mis/ReportQueue";
import VehicleInAndOut from "./screens/mis/VehicleInAndOut";
import MISReport from "./screens/transaction/MISReport";
import PerformanceFinanceReport from "./screens/transaction/PerformanceFinanceReport";
import QuickRequestCreation from "./screens/transaction/QuickRequestCreation";
import RevenueDistributionReport from "./screens/transaction/RevenueDistributionReport";
import ShiftAssignment from "./screens/transaction/ShiftAssignment";
import JobModification from "./screens/support/JobModification";
import FinanceAmendment from "./screens/support/FinanceAmendment";
import RuleAssignment from "./screens/mis/RuleAssignment";
import EmployeeModification from "./screens/support/EmployeeModification";
import VehicleModification from "./screens/support/VehicleModification";
import IssueManagement from "./screens/fleet/IssueManagement";
import LocationApproval from "./screens/master/LocationApproval";
import ExternalJobDashboard from "./screens/mission-control/ExternalJobDashboard";
import SupplierPaymentHold from "./screens/finance/SupplierPaymentHold";
import FuelRequest from "./screens/transaction/FuelRequest";

dayjs.extend(timezone);

enableMapSet();

function App() {
  const needAuth = useSelector((state) => state.user.needAuth);
  const user = useSelector((state) => state.user?.userDetails);
  const permissionStatus = useSelector((state) => state.user.permissionStatus);
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.user.permissions[MODULES.Master]);
  const fleetAuth = useSelector(
    (state) => state.user.permissions[MODULES.Fleet],
  );
  const company = useUserCompany();

  useEffect(() => {
    if (company?.comTimezone) {
      dayjs.tz.setDefault(company.comTimezone);
    }
  }, [company?.comTimezone]);

  const mainDashboardPermission =
    auth?.[FORMS?.MAIN_DASHBOARD?.DASHBOARD?.form] ?? {};
  const fleetDashboardPermission =
    fleetAuth?.[FORMS?.FLEET_DASHBORARD?.DASHBORARD?.form] ?? {};
  useEffect(() => {
    if (permissionStatus === statusConst.IDLE && !needAuth) {
      dispatch(loadPermissions());
    }
  }, [dispatch, needAuth, permissionStatus]);

  console.log("fleetDashboardPermission", fleetDashboardPermission);

  return (
    <>
      <NotificationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomBrowserRouter>
            {/*{!needAuth && <SeasonalBanner user={user} />}*/}
            <Routes>
              <Route path="login" element={<Login />} />
              <Route path="mobile-callback" element={<MobileCallback />} />
              <Route path="shortenLink" element={<ShortenLinkCallback />} />
              <Route path="recovery" element={<PasswordRecoveryWizard />} />
              <Route
                path="external/track/:jobId"
                element={<ExternalJobDashboard />}
              />
              <Route element={<AuthGuard />}>
                <Route path="/" element={<Layout />}>
                  <Route path="unauthorized" element={<Unauthorized />} />
                  <Route
                    path="transaction/quick-request"
                    element={<QuickRequestCreation />}
                  />
                  <Route
                    index
                    element={
                      mainDashboardPermission &&
                      Object.keys(mainDashboardPermission).length > 0 ? (
                        <Dashboard />
                      ) : fleetDashboardPermission &&
                        Object.keys(fleetDashboardPermission).length > 0 ? (
                        <Navigate to="/fleet/fleet-dashboard" replace />
                      ) : (
                        <Dashboard />
                      )
                    }
                  />
                  <Route path="grid" element={<GridExample />} />
                  <Route path="formex" element={<FormExample />} />
                  <Route path="charts" element={<Chart />} />
                  <Route path="example/wizard" element={<Wizard />} />
                  <Route path="mission-control" element={<MissionControl />} />
                  <Route path="master/account" element={<Account />} />
                  <Route path="master/stakeholder" element={<Stakeholder />} />
                  <Route path="master/agreements" element={<Agreements />} />
                  <Route
                    path="master/agreement-template"
                    element={<AgreementTemplate />}
                  />
                  <Route path="master/employee" element={<Employee />} />
                  <Route
                    path="/master/cluster-creation"
                    element={<ClusterMaster />}
                  />
                  <Route
                    path="/master/stakeholder-group-creation"
                    element={<StakeholderGroup />}
                  />
                  <Route path="/master/trailers" element={<Trailers />} />
                  <Route path="master/blacklist" element={<BlackList />} />
                  <Route path="master/gs-division" element={<GSDivision />} />
                  <Route
                    path="master/location-creation"
                    element={<LocationCreation />}
                  />
                  <Route
                    path="master/location-approval"
                    element={<LocationApproval />}
                  />
                  <Route
                    path="transaction/request-creation"
                    element={<RequestCreationNew />}
                  />
                  <Route path="master/account" element={<Account />} />
                  <Route path="master/document" element={<Document />} />
                  <Route path="master/vehicle" element={<Vehicle />} />
                  <Route path="master/vehicle-type" element={<VehicleType />} />
                  <Route path="master/bank" element={<Bank />} />
                  <Route
                    path="master/police-division"
                    element={<PoliceDivision />}
                  />
                  <Route path="master/bank-branch" element={<BankBranch />} />
                  <Route path="master/stakeholder" element={<Stakeholder />} />
                  <Route
                    path="master/item-creation"
                    element={<DeductionItem />}
                  />
                  <Route
                    path="master/deduction-approval"
                    element={<ApprovalMaster />}
                  />
                  <Route
                    path="fleet/template-create"
                    element={<DynamicTemplate />}
                  />
                  <Route
                    path="transaction/request-creation"
                    element={<RequestCreationNew />}
                  />
                  <Route
                    path="transaction/request-approval"
                    element={<RequestApproval />}
                  />
                  <Route
                    path="transaction/approval/supplier-negotiation"
                    element={<SupplierNegotiationApproval />}
                  />
                  <Route path="transaction/ncr-upload" element={<NCR />} />
                  <Route
                    path="transaction/job-monitoring/:id/:popup"
                    element={<JobMonitor />}
                  />
                  <Route
                    path="transaction/job-monitoring"
                    element={<JobMonitor />}
                  />
                  <Route path="mis/mis-report" element={<MISReport />} />
                  <Route
                    path="finance/ev-inv-generator"
                    element={<EvinvGenerator />}
                  />
                  <Route
                    path="finance/transporter-invoice"
                    element={<TransporterInvoice />}
                  />
                  <Route
                    path="finance/ev-inv-approve"
                    element={<EvInvApproval />}
                  />
                  <Route
                    path="finance/tax-management"
                    element={<TaxManagement />}
                  />
                  <Route path="finance/charge-type" element={<ChargeType />} />
                  <Route
                    path="finance/invoice-search"
                    element={<InvoiceSearch />}
                  />
                  <Route
                    path="finance/rate-manager"
                    element={<RateManager />}
                  />
                  <Route path="finance/charge" element={<ChargeCreation />} />
                  <Route
                    path="finance/charge-confirmation"
                    element={<ChargeConfirmation />}
                  />
                  <Route
                    path="finance/exchangeRate"
                    element={<ExchangeRate />}
                  />
                  <Route
                    path="finance/approvalProcess"
                    element={<ApprovalProcess />}
                  />
                  <Route
                    path="finance/deduction-master"
                    element={<DeductionMaster />}
                  />
                  <Route
                    path="finance/credit-debit-note"
                    element={<CreditDebitNote />}
                  />
                  <Route
                    path="finance/oracle-process-queue"
                    element={<OracleProcessQueue />}
                  />
                  <Route
                    path="fleet/document-management"
                    element={<DocumentManagement />}
                  />
                  <Route
                    path="fleet/fleet-dashboard"
                    element={<FleetDashboard />}
                  />
                  <Route
                    path="fleet/inspection-process"
                    element={<DocumentManagement />}
                  />
                  <Route
                    path="fleet/inspection-create"
                    element={<DocumentManagement />}
                  />

                  <Route path="fleet/issues" element={<IssueManagement />} />
                  <Route path="fleet/work-order" element={<WorkOrder />} />
                  <Route
                    path="master/geographical-management"
                    element={<Geographical />}
                  />
                  <Route
                    path="master/call-center-shift"
                    element={<ShiftMaster />}
                  />
                  <Route
                    path="master/call-center-shift-group"
                    element={<ShiftGroupMaster />}
                  />
                  <Route
                    path="transaction/call-center-shift"
                    element={<ShiftAssignment />}
                  />
                  <Route
                    path="audit/audit-report"
                    element={<AuditReportSearch />}
                  />
                  <Route
                    path="audit/audit-report"
                    element={<AuditReportSearch />}
                  />
                  <Route
                    path="mis/performance-monitor"
                    element={<PerformanceMonitoring />}
                  />
                  <Route
                    path={"mis/vehicle-in-out"}
                    element={<VehicleInAndOut />}
                  />
                  <Route
                    path={"mis/document-delivery-note"}
                    element={<DocumentDeliveryNote />}
                  />
                  <Route
                    path="mis/report-generator"
                    element={<ReportGenerator />}
                  >
                    <Route path="queue" element={<ReportQueue />} />
                    <Route path="queue/:id" element={<ReportQueue />} />
                    <Route path="rule-creation" element={<RuleAssignment />} />
                  </Route>
                  <Route
                    path={"/mis/call-center-performance"}
                    element={<CallCenterPerformance />}
                  />
                  <Route
                    path={"/mis/deduction"}
                    element={<DeductionReport />}
                  />
                  {/* <Route
                    path={"mis/report-generator"}
                    element={<ReportGenerator />}
                  /> */}
                  <Route path="admin/default-alert" element={<Alert />} />
                  <Route
                    path="/mis/finance-analytics"
                    element={<FinanceAnalytics />}
                  />
                  <Route
                    path="/mis/live-analytics"
                    element={<LiveAnalytics />}
                  />
                  <Route
                    path="/revenue/revenue-report"
                    element={<RevenueDistributionReport />}
                  />
                  <Route
                    path="/performance/finance-report"
                    element={<PerformanceFinanceReport />}
                  />
                  <Route
                    path="admin/option-config"
                    element={<OptionConfig />}
                  />
                  <Route
                    path="/support/job-modification"
                    element={<JobModification />}
                  />
                  <Route
                    path="/support/finance-amendment"
                    element={<FinanceAmendment />}
                  />

                  <Route
                    path="/support/employee-modification"
                    element={<EmployeeModification />}
                  />
                  <Route
                    path="/support/vehicle-modification"
                    element={<VehicleModification />}
                  />
                  <Route
                      path="finance/supplier-payment-hold"
                      element={<SupplierPaymentHold />}
                  />
                  <Route
                      path="/transaction/fuel-request-panel"
                      element={<FuelRequest />}
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </CustomBrowserRouter>
        </LocalizationProvider>
      </NotificationProvider>
      {createPortal(
        <ToastContainer
          enableMultiContainer={true}
          containerId="Main"
          hideProgressBar
          zIndex={10}
        />,
        document.getElementById("toast-root"),
        document.getElementById("toast-root"),
      )}
    </>
  );
}

export default App;
